/**
 * VideoWalkSummary — post-recording review screen.
 *
 * Prioritizes: headline, bullets, contractor-stated measurements (with
 * provenance), Levi observations, and Levi follow-up questions.
 * The full transcript is available under a "View Transcript" toggle so
 * transcript editing is NOT the dominant workflow.
 */
import { useMemo, useState } from "react";
import {
  Sparkles, Ruler, HelpCircle, ClipboardList, ChevronDown, ChevronUp,
  Video, Mic, ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";

const provenanceLabel = (m) =>
  m.source === "contractor"
    ? "Contractor stated"
    : m.source === "levi_derived"
    ? `Levi derived · ${m.formula || "computed"}`
    : m.source;

export default function VideoWalkSummary({ session, onStartAnother, primaryCta }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const [answering, setAnswering] = useState({});
  const [local, setLocal] = useState(session);

  const transcriptText = useMemo(
    () => (local.video_walk_transcript?.text || "").trim(),
    [local]
  );

  const submitAnswer = async (qid, text) => {
    setAnswering((s) => ({ ...s, [qid]: true }));
    try {
      const r = await api.post(
        `/video-walks/${local.id}/questions/${qid}/answer`,
        { answer: text }
      );
      setLocal(r.data);
    } finally {
      setAnswering((s) => ({ ...s, [qid]: false }));
    }
  };

  return (
    <div className="space-y-4" data-testid="video-walk-summary">
      <div className="rounded-xl border border-[var(--jc-border)] bg-[var(--jc-surface)] p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-blue-100 p-2 shrink-0">
            <Sparkles size={16} className="text-blue-700" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">
              Video Job Walk Summary
            </div>
            <div className="text-lg font-semibold mt-1" data-testid="video-walk-summary-headline">
              {local.video_walk_summary?.headline || "Video Job Walk captured."}
            </div>
            {local.video_walk_summary?.bullet_points?.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm">
                {local.video_walk_summary.bullet_points.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[var(--jc-text-soft)]">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Contractor-stated measurements + Levi-derived */}
      {(local.video_walk_measurements || []).length > 0 && (
        <div className="rounded-lg border border-[var(--jc-border)] p-4">
          <div className="flex items-center gap-2 text-sm font-semibold mb-3">
            <Ruler size={14} /> Measurements
          </div>
          <ul className="space-y-2" data-testid="video-walk-measurements">
            {local.video_walk_measurements.map((m) => (
              <li key={m.id} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="font-medium">{m.label}</span>
                <span className="font-mono">
                  {m.value} {m.unit}
                </span>
                <span className="text-xs text-[var(--jc-text-soft)] italic">
                  {provenanceLabel(m)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Levi observations */}
      {(local.video_walk_observations || []).length > 0 && (
        <div className="rounded-lg border border-[var(--jc-border)] p-4">
          <div className="flex items-center gap-2 text-sm font-semibold mb-2">
            <ClipboardList size={14} /> Levi observations
          </div>
          <ul className="space-y-1 text-sm" data-testid="video-walk-observations">
            {local.video_walk_observations.map((o) => (
              <li key={o.id} className="text-[var(--jc-text-soft)]">• {o.text}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Levi follow-up questions */}
      {(local.video_walk_questions || []).length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 mb-2">
            <HelpCircle size={14} /> Levi follow-up questions
          </div>
          <ul className="space-y-3" data-testid="video-walk-questions">
            {local.video_walk_questions.map((q) => (
              <li key={q.id} className="text-sm text-amber-900">
                <div className="font-medium">{q.question}</div>
                {q.status === "answered" ? (
                  <div className="mt-1 text-xs italic">Your answer: {q.contractor_answer}</div>
                ) : (
                  <form
                    className="mt-2 flex gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const ans = e.currentTarget.elements.namedItem("ans").value.trim();
                      if (ans) submitAnswer(q.id, ans);
                    }}
                  >
                    <input
                      name="ans"
                      placeholder="Your answer"
                      className="flex-1 rounded border border-amber-300 bg-white px-2 py-1 text-sm"
                      data-testid={`video-walk-question-answer-input-${q.id}`}
                    />
                    <button
                      type="submit"
                      disabled={!!answering[q.id]}
                      className="text-xs rounded bg-amber-900 text-white px-3 py-1"
                      data-testid={`video-walk-question-answer-submit-${q.id}`}
                    >
                      Save
                    </button>
                  </form>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Segments (technical detail — hidden by default) */}
      {(local.segments || []).length > 0 && (
        <details className="rounded-lg border border-[var(--jc-border)]" data-testid="video-walk-segments-details">
          <summary className="p-3 text-sm text-[var(--jc-text-soft)] cursor-pointer">
            Recording timeline · {local.segments.length} clip{local.segments.length === 1 ? "" : "s"}
          </summary>
          <ul className="border-t border-[var(--jc-border)] divide-y divide-[var(--jc-border)]">
            {local.segments.map((s) => (
              <li key={s.id} className="p-3 text-xs flex items-center gap-3">
                {s.kind === "video" ? <Video size={12} /> : <Mic size={12} />}
                <span className="font-mono">{s.section_label || `Clip ${s.index + 1}`}</span>
                <span className="text-[var(--jc-text-soft)]">
                  {Math.round(s.duration_seconds)}s · {Math.round((s.size_bytes || 0) / 1024)} KB · {s.status}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Transcript — available but not the primary workflow */}
      <div className="rounded-lg border border-[var(--jc-border)]">
        <button
          type="button"
          className="w-full flex items-center justify-between p-3 text-sm"
          onClick={() => setShowTranscript((v) => !v)}
          data-testid="video-walk-view-transcript-btn"
        >
          <span className="text-[var(--jc-text-soft)]">
            {showTranscript ? "Hide transcript" : "View Transcript"}
          </span>
          {showTranscript ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {showTranscript && (
          <div className="border-t border-[var(--jc-border)] p-3 text-sm whitespace-pre-wrap max-h-72 overflow-y-auto" data-testid="video-walk-transcript-text">
            {transcriptText || <span className="italic text-[var(--jc-text-soft)]">Transcript is empty.</span>}
          </div>
        )}
      </div>

      {primaryCta && (
        <div className="pt-2" data-testid="video-walk-summary-primary-cta-wrap">
          <button
            type="button"
            onClick={primaryCta.onClick}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-md bg-black text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90"
            data-testid="video-walk-summary-primary-cta"
          >
            {primaryCta.label} <ArrowRight size={14} />
          </button>
        </div>
      )}

      {onStartAnother && (
        <div>
          <button
            type="button"
            onClick={onStartAnother}
            className="text-sm text-[var(--jc-text-soft)] underline"
            data-testid="video-walk-start-another-btn"
          >
            Record another Video Job Walk on this project
          </button>
        </div>
      )}
    </div>
  );
}
